import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { HiLocationMarker, HiMail, HiPaperAirplane, HiPhone } from 'react-icons/hi';
import emailjs from '@emailjs/browser';

const COMPANY_EMAIL = 'ecosistemas.computacao@gmail.com';
const SENDER_NAME = 'Site Ecosistemas';
const WHATSAPP_MESSAGE = encodeURIComponent('Olá Ecosistemas, gostaria de saber mais dos seus produtos.');
const WHATSAPP_URL = `https://wa.me/5567984485377?text=${WHATSAPP_MESSAGE}`;
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_zszx348';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_n185x18';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'ZPrpcdCHdg_BBv4fn';

const cardStyle = {
  background: 'rgba(255,255,255,0.92)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderRadius: '28px',
  padding: '40px',
  border: '1px solid rgba(59,158,212,0.14)',
  boxShadow: '0 12px 36px rgba(59,158,212,0.08)',
  height: '100%',
};

const fieldBaseStyle = {
  width: '100%',
  padding: '15px 18px',
  fontSize: '1rem',
  border: '1px solid rgba(59, 158, 212, 0.18)',
  borderRadius: '18px',
  background: 'rgba(235,246,252,0.56)',
  color: '#1A2D3A',
  transition: 'all 0.25s ease',
  fontFamily: 'Inter, sans-serif',
};

const sectionVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      staggerChildren: 0.08,
    },
  },
};

const itemVariant = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0 },
};

function ContactInfoItem({ icon: Icon, color, title, children }) {
  return (
    <motion.div
      variants={itemVariant}
      whileHover={{ x: 4 }}
      style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '26px' }}
    >
      <div
        style={{
          width: '52px',
          height: '52px',
          background: `${color}18`,
          border: `1px solid ${color}35`,
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon size={22} color={color} />
      </div>
      <div style={{ color: '#4A6680', lineHeight: 1.7 }}>
        <p style={{ fontWeight: 700, color: '#1A2D3A', marginBottom: '4px' }}>{title}</p>
        {children}
      </div>
    </motion.div>
  );
}

function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '', company: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const emailReady = useMemo(
    () => EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY,
    []
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formData.company) {
      return;
    }

    if (!emailReady) {
      setStatus({
        type: 'error',
        message: 'O formulário está temporariamente indisponível. Use o WhatsApp ou telefone para falar com a equipe.',
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const templateParams = {
        to_email: COMPANY_EMAIL,
        to_name: 'Ecosistemas Computacao',
        sender_name: SENDER_NAME,
        contact_name: formData.name,
        contact_email: formData.email,
        from_name: formData.name,
        from_email: formData.email,
        reply_to: formData.email,
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      setStatus({
        type: 'success',
        message: 'Mensagem enviada com sucesso. A equipe deve retornar em breve.',
      });
      setFormData({ name: '', email: '', subject: '', message: '', company: '' });
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Não foi possível enviar agora. Tente novamente ou use o WhatsApp.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusStyles = {
    success: {
      background: 'rgba(16, 163, 127, 0.10)',
      border: '1px solid rgba(16, 163, 127, 0.24)',
      color: '#0F766E',
    },
    error: {
      background: 'rgba(239, 68, 68, 0.08)',
      border: '1px solid rgba(239, 68, 68, 0.20)',
      color: '#B91C1C',
    },
  };

  return (
    <section id="contact" className="section-padding" style={{ background: 'linear-gradient(180deg, #FAFEFF 0%, #EBF6FC 100%)' }}>
      <div className="container">
        <motion.div
          className="section-header text-center"
          initial="hidden"
          animate="visible"
          variants={sectionVariants}
        >
          <motion.p
            variants={itemVariant}
            style={{
              display: 'inline-flex',
              padding: '8px 14px',
              borderRadius: '9999px',
              background: 'rgba(59,158,212,0.08)',
              color: '#2178AB',
              fontWeight: 700,
              marginBottom: '18px',
            }}
          >
            Fale com a equipe
          </motion.p>
          <motion.h2 variants={itemVariant} className="section-title">
            Vamos entender seu processo e montar a solução certa.
          </motion.h2>
          <motion.p variants={itemVariant} style={{ maxWidth: '620px', margin: '0 auto 40px', color: '#4A6680' }}>
            Se você já tem um problema concreto ou quer estruturar um novo sistema, este é o melhor ponto de partida.
          </motion.p>
        </motion.div>

        <div className="row" style={{ rowGap: '24px' }}>
          <div className="col-lg-7 col-md-12">
            <motion.div
              style={cardStyle}
              initial="hidden"
              animate="visible"
              variants={sectionVariants}
            >
              <motion.h3 variants={itemVariant} style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#1A2D3A' }}>
                Conte rapidamente o que você precisa
              </motion.h3>
              <motion.p variants={itemVariant} style={{ color: '#5A7A8E', marginBottom: '28px', lineHeight: 1.7 }}>
                Quanto mais claro o contexto, melhor a primeira conversa.
              </motion.p>

              <form onSubmit={handleSubmit}>
                <div className="sr-only" aria-hidden="true">
                  <label htmlFor="company">Empresa</label>
                  <input id="company" name="company" type="text" value={formData.company} onChange={handleChange} tabIndex="-1" autoComplete="off" />
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <label htmlFor="name" style={{ display: 'block', marginBottom: '8px', color: '#1A2D3A', fontWeight: 600 }}>
                      Nome
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Seu nome"
                      required
                      autoComplete="name"
                      style={fieldBaseStyle}
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="email" style={{ display: 'block', marginBottom: '8px', color: '#1A2D3A', fontWeight: 600 }}>
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="voce@empresa.com"
                      required
                      autoComplete="email"
                      inputMode="email"
                      style={fieldBaseStyle}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '18px' }}>
                  <label htmlFor="subject" style={{ display: 'block', marginBottom: '8px', color: '#1A2D3A', fontWeight: 600 }}>
                    Assunto
                  </label>
                  <input
                    id="subject"
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Ex.: ERP para clínica, sistema sob medida, integração fiscal"
                    required
                    style={fieldBaseStyle}
                  />
                </div>

                <div style={{ marginTop: '18px' }}>
                  <label htmlFor="message" style={{ display: 'block', marginBottom: '8px', color: '#1A2D3A', fontWeight: 600 }}>
                    Mensagem
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Descreva o processo atual, a dificuldade principal e o que você espera resolver."
                    rows="6"
                    required
                    style={{ ...fieldBaseStyle, resize: 'vertical', minHeight: '160px' }}
                  />
                </div>

                {status.message && (
                  <div
                    aria-live="polite"
                    style={{
                      ...statusStyles[status.type],
                      borderRadius: '18px',
                      padding: '14px 16px',
                      marginTop: '20px',
                      lineHeight: 1.6,
                    }}
                  >
                    {status.message}
                  </div>
                )}

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={!isSubmitting ? { y: -2, scale: 1.01 } : undefined}
                  whileTap={!isSubmitting ? { scale: 0.99 } : undefined}
                  style={{
                    marginTop: '24px',
                    background: 'linear-gradient(135deg, #3B9ED4 0%, #6BBDDF 100%)',
                    color: '#fff',
                    padding: '16px 26px',
                    borderRadius: '9999px',
                    fontSize: '1rem',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    boxShadow: '0 10px 24px rgba(59,158,212,0.22)',
                    opacity: isSubmitting ? 0.75 : 1,
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        style={{
                          width: '18px',
                          height: '18px',
                          border: '2px solid rgba(255,255,255,0.38)',
                          borderTopColor: '#fff',
                          borderRadius: '50%',
                        }}
                      />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <HiPaperAirplane size={20} />
                      Enviar mensagem
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>
          </div>

          <div className="col-lg-5 col-md-12">
            <motion.div
              style={cardStyle}
              initial="hidden"
              animate="visible"
              variants={sectionVariants}
            >
              <motion.h3 variants={itemVariant} style={{ fontSize: '1.5rem', marginBottom: '14px', color: '#1A2D3A' }}>
                Canais diretos
              </motion.h3>
              <motion.p variants={itemVariant} style={{ color: '#5A7A8E', marginBottom: '28px', lineHeight: 1.7 }}>
                Se preferir, fale com a equipe por WhatsApp, telefone ou email.
              </motion.p>

              <ContactInfoItem icon={HiLocationMarker} color="#3B9ED4" title="Endereço">
                <p>R. Ten. Bernardes n° 790 - Centro</p>
                <p>Jardim - Mato Grosso do Sul</p>
                <p>Brasil, 79240-000</p>
              </ContactInfoItem>

              <ContactInfoItem icon={HiMail} color="#2178AB" title="Email">
                <a href="mailto:ecosistemas.computacao@gmail.com" style={{ color: '#2178AB', fontWeight: 600 }}>
                  ecosistemas.computacao@gmail.com
                </a>
              </ContactInfoItem>

              <ContactInfoItem icon={HiPhone} color="#5BACD1" title="Telefone">
                <a href="tel:+5567984485377" style={{ color: '#4A6680', fontWeight: 600 }}>
                  (67) 98448-5377
                </a>
              </ContactInfoItem>

              <motion.a
                variants={itemVariant}
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -2, scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                  color: '#fff',
                  padding: '16px 24px',
                  borderRadius: '9999px',
                  fontWeight: 700,
                  marginTop: '16px',
                  boxShadow: '0 10px 24px rgba(37,211,102,0.22)',
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Falar no WhatsApp
              </motion.a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
